/**
 * MAHI — Home (main stage + "See This On Me" CTA).
 * One dress shown at a time with left/right navigation. Try-on CTA calls our
 * MAHI backend which (in MVP-1) returns a mocked result.
 *
 * The profile photo is the one thing try-on actually needs and it is asked
 * for right here, the moment it's needed — not during sign-up.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ChevronLeft, ChevronRight, Heart, Search, ShoppingBag, Sparkles, LogOut, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import mira, { trackEvent , errMsg } from '../api';
import { useMiraAuth } from '../auth';
import MiraRobot from '../components/MiraRobot';
import MiraShell from '../components/MiraShell';

export default function MiraHome() {
  const navigate = useNavigate();
  const { user, signOut, refresh } = useMiraAuth();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tryonBusy, setTryonBusy] = useState(false);
  const [tryonResult, setTryonResult] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [showPhotoPrompt, setShowPhotoPrompt] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { document.title = 'MAHI'; }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await mira.get('/inventory/dresses?limit=40');
        setItems(data.items || []);
      } catch { toast.error('Could not load dresses'); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    mira.get('/profile/completeness').then((r) => setCompleteness(r.data)).catch(() => {});
  }, [user?.picture, user?.email]);

  const dress = items[idx];
  const remaining = useMemo(() =>
    Math.max((user?.tryons_quota || 0) - (user?.tryons_used || 0), 0), [user]);

  const next = () => { setTryonResult(null); setIdx((i) => Math.min(i + 1, items.length - 1)); trackEvent('dress_viewed', { mira_sku: items[idx + 1]?.mira_sku, direction: 'next' }); };
  const prev = () => { setTryonResult(null); setIdx((i) => Math.max(i - 1, 0)); };

  const runTryOn = async () => {
    if (!dress) return;
    setTryonBusy(true);
    trackEvent('tryon_started', { mira_sku: dress.mira_sku });
    try {
      const { data } = await mira.post('/tryon', { mira_sku: dress.mira_sku });
      setTryonResult(data.output_image_url);
      trackEvent('tryon_completed', { mira_sku: dress.mira_sku, mocked: data.mocked });
      toast.success(data.mocked ? 'Preview ready (mocked in MVP-1)' : 'Try-on ready');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Try-on failed';
      toast.error(msg);
      trackEvent('tryon_failed', { mira_sku: dress.mira_sku, error: msg });
    } finally { setTryonBusy(false); }
  };

  const tryItOn = () => {
    if (!dress) return;
    // First time trying anything on with no photo yet — this is the one
    // moment a photo is actually required, so ask for it right here.
    if (!user?.picture) { setShowPhotoPrompt(true); return; }
    runTryOn();
  };

  const uploadPhotoThenTryOn = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image');
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await mira.post('/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      trackEvent('profile_photo_uploaded', { context: 'tryon_prompt' });
      await refresh();
      setShowPhotoPrompt(false);
      runTryOn();
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); }
    finally { setPhotoBusy(false); }
  };

  return (
    <MiraShell>
      <div className="min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="px-5 sm:px-8 pt-6 pb-3 flex items-center justify-between gap-4">
          {/* user */}
          <button onClick={() => navigate('/mira/profile')} className="flex items-center gap-3 text-left" data-testid="mira-profile">
            <div className="w-10 h-10 rounded-full bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
              {user?.picture
                ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                : <span className="text-sm font-semibold text-slate-500">{(user?.name || 'U').slice(0,1).toUpperCase()}</span>}
            </div>
            <div className="hidden sm:block">
              <div className="text-xs text-slate-400">Welcome back</div>
              <div className="text-sm font-medium text-slate-800">{user?.name || user?.phone}</div>
            </div>
          </button>

          {/* search */}
          <div className="flex-1 max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="mira-search"
              placeholder="Search, paste a URL, or describe the look…"
              className="w-full rounded-full bg-white border border-slate-200 pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
            />
          </div>

          {/* studio */}
          <button onClick={() => navigate('/mira/profile')} data-testid="mira-studio-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300">
            <Sparkles className="w-4 h-4 text-amber-400" /> My Style Studio
          </button>
          <button onClick={signOut} title="Sign out" className="text-slate-400 hover:text-slate-700">
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        {/* completeness nudge — dismissible, never a gate */}
        {completeness && completeness.percent < 100 && (
          <button onClick={() => navigate('/mira/profile')}
            className="mx-5 sm:mx-8 mb-1 flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 transition rounded-full px-3.5 py-2 w-fit">
            <span className="font-semibold text-slate-700">{completeness.percent}%</span>
            profile complete — add {completeness.missing[0]?.replace('_', ' ') || 'a few things'} for better picks
          </button>
        )}

        {/* Main stage */}
        <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-6">
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          ) : !dress ? (
            <p className="text-sm text-slate-500">No dresses yet.</p>
          ) : (
            <div className="w-full max-w-5xl">
              <div className="flex items-center justify-center gap-3 sm:gap-6">
                <button onClick={prev} disabled={idx === 0} className="p-2 rounded-full bg-white border border-slate-200 disabled:opacity-30">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={dress.mira_sku}
                    initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.35 }}
                    className="relative bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)] overflow-hidden w-[min(420px,80vw)] aspect-[3/4]"
                  >
                    <img src={tryonResult || dress.image_urls?.[0]} alt={dress.title}
                         className="absolute inset-0 w-full h-full object-cover" />
                    {tryonResult && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase bg-white/90 backdrop-blur px-2.5 py-1 rounded-full font-semibold text-slate-700">
                        <Sparkles className="w-3 h-3 text-amber-400" /> on you
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
                <button onClick={next} disabled={idx >= items.length - 1} className="p-2 rounded-full bg-white border border-slate-200 disabled:opacity-30">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* meta */}
              <div className="text-center mt-7 mb-5">
                <div className="text-[10px] tracking-[0.32em] uppercase text-slate-400 font-semibold">{dress.brand}</div>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-1">{dress.title}</h2>
                <p className="text-sm text-slate-500 mt-1.5 max-w-xl mx-auto">{dress.description}</p>
                <div className="mt-3 flex items-center justify-center gap-3 text-sm">
                  <span className="text-slate-400 line-through">₹{Number(dress.price).toLocaleString()}</span>
                  <span className="font-semibold text-slate-900">₹{Number(dress.offer_price).toLocaleString()}</span>
                </div>
              </div>

              {/* primary CTA */}
              <div className="flex flex-col items-center">
                <button onClick={tryItOn} disabled={tryonBusy || remaining <= 0} data-testid="see-on-me-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-7 py-3.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 shadow-[0_20px_40px_-15px_rgba(15,23,42,0.4)]">
                  {tryonBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 text-amber-300" /> See This On Me</>}
                </button>
                <div className="mt-3 text-xs text-slate-500" data-testid="tryon-counter">
                  {user?.tryons_used || 0} / {user?.tryons_quota || 0} try-ons used
                  {remaining <= 0 && <span className="ml-2 text-amber-600">Upgrade to MAHI Plus for more</span>}
                </div>
              </div>

              {/* bottom actions */}
              <div className="mt-8 flex items-center justify-between text-sm text-slate-500">
                <button data-testid="wishlist-btn"
                  onClick={() => { trackEvent('wishlist_added', { mira_sku: dress.mira_sku }); toast.success('Saved to wishlist'); }}
                  className="inline-flex items-center gap-2 hover:text-rose-500">
                  <Heart className="w-4 h-4" /> Wishlist
                </button>
                <a data-testid="buy-it-now-btn"
                   href={dress.product_url} target="_blank" rel="noreferrer"
                   onClick={() => trackEvent('buy_clicked', { mira_sku: dress.mira_sku })}
                   className="inline-flex items-center gap-2 hover:text-slate-900">
                  Buy it now <ShoppingBag className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}
        </main>

        {/* corner robot */}
        <div className="fixed bottom-5 right-5 z-30 pointer-events-none">
          <MiraRobot size={72} />
        </div>

        {/* contextual photo prompt — only ever shown right before a try-on */}
        <AnimatePresence>
          {showPhotoPrompt && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center px-5"
              onClick={() => !photoBusy && setShowPhotoPrompt(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.3)] p-7 text-center"
              >
                <button onClick={() => !photoBusy && setShowPhotoPrompt(false)} className="absolute mt-[-12px] ml-[calc(100%-40px)] text-slate-300 hover:text-slate-500">
                  <X className="w-4 h-4" />
                </button>
                <div onClick={() => fileRef.current?.click()}
                     className="cursor-pointer w-28 h-28 mx-auto rounded-3xl bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center overflow-hidden mb-5 relative">
                  <Camera className="w-8 h-8 text-slate-300" />
                  {photoBusy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">One photo to see this on you</h3>
                <p className="text-sm text-slate-500 mb-5">Full-length, good lighting, front-facing works best.</p>
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => uploadPhotoThenTryOn(e.target.files?.[0])} />
                <button onClick={() => fileRef.current?.click()} disabled={photoBusy} data-testid="tryon-photo-upload-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium hover:bg-slate-800 disabled:opacity-50">
                  {photoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload photo'}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MiraShell>
  );
}
